import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageContainer from '@/components/Container/PageContainer';
import { Search } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import TableStatus from '@/components/UI/TableStatus';
import useResize from '@/hooks/useResize';
import type { TableRequestState } from '@/types/table';
import { translatePblCaseStatusLabel } from '@/utils/pblCaseStatus';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';

import { filterPblCaseListRows } from './caseListState';
import { isPblDashboardCaseFilter } from './dashboardFilter';
import {
  CaseListShell,
  CasesTable,
  ConfirmButton,
  FilterBar,
  FilterButton,
  PageButton,
  PageSelect,
  PaginationGroup,
  SearchIconButton,
  SearchInput,
  StatusBadge,
  AttentionLabel,
  TableCard,
  TableFooter,
  TableScroll,
  TitleBar,
} from './styles';
import type {
  PblCaseListAction,
  PblCaseListColumn,
  PblCaseListFilter,
  PblCaseListRow,
  PblCaseListStatus,
} from './types';

type PblCaseListProps<TRow extends PblCaseListRow> = {
  title: string;
  backTo?: string | number;
} & PblCaseListContentProps<TRow>;

type PblCaseListContentProps<TRow extends PblCaseListRow> = {
  rows: TRow[];
  columns?: PblCaseListColumn[];
  mobileCardLayout?: boolean;
  tabletCardLayout?: boolean;
  embedded?: boolean;
  stripedRows?: boolean;
  filters?: Array<{ value: PblCaseListFilter; label: string }>;
  initialFilter?: PblCaseListFilter;
  onFilterChange?: (filter: PblCaseListFilter) => void;
  primaryAction?: PblCaseListAction;
  totalEntries?: number;
  currentPage?: number;
  rowsPerPage?: number;
  lastPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  searchAriaLabel: string;
  statusLabels?: Partial<Record<PblCaseListStatus, string>>;
  requestState?: TableRequestState;
  loadingLabel?: string;
  refreshingLabel?: string;
  emptyLabel?: string;
  errorLabel?: string;
  retryLabel?: string;
  onRetry?: () => void;
  isRowClickable?: (item: TRow) => boolean;
  onRowClick: (item: TRow) => void;
};

export const PblCaseListContent = <TRow extends PblCaseListRow>({
  rows,
  columns,
  mobileCardLayout = false,
  tabletCardLayout = false,
  embedded = false,
  stripedRows = false,
  filters,
  initialFilter = 'all',
  onFilterChange,
  primaryAction,
  totalEntries,
  currentPage,
  rowsPerPage,
  lastPage,
  onPageChange,
  onRowsPerPageChange,
  searchAriaLabel,
  statusLabels = {},
  requestState = { status: 'success', error: null },
  loadingLabel,
  refreshingLabel,
  emptyLabel,
  errorLabel,
  retryLabel,
  onRetry,
  isRowClickable = () => true,
  onRowClick,
}: PblCaseListContentProps<TRow>) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const tr = (key: string, defaultValue: string) => t(key as any, { defaultValue }) as string;
  const { isMobile } = useResize();
  const [filter, setFilter] = useState<PblCaseListFilter>(initialFilter);
  const [keyword, setKeyword] = useState('');
  const [localPage, setLocalPage] = useState(1);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(10);
  const defaultColumns = useMemo<PblCaseListColumn[]>(
    () => [
      { key: 'caseId', label: tr('pbl.lawyerMatching.caseList.columns.caseId', 'Case ID') },
      {
        key: 'applicantName',
        label: tr('pbl.lawyerMatching.caseList.columns.applicantName', 'Applicant Name'),
      },
      {
        key: 'legalServiceNeeds',
        label: tr('pbl.lawyerMatching.caseList.columns.legalServiceNeeds', 'Legal Service Requirements'),
      },
      {
        key: 'creationDate',
        label: tr('pbl.lawyerMatching.caseList.columns.creationDate', 'Date Created'),
      },
      { key: 'status', label: tr('pbl.lawyerMatching.caseList.columns.status', 'Status') },
    ],
    [t]
  );
  const defaultStatusLabels = useMemo<Record<PblCaseListStatus, string>>(
    () => ({
      in_progress: tr('pbl.lawyerMatching.caseList.statuses.inProgress', 'In Progress'),
      to_be_rated: tr('pbl.lawyerMatching.caseList.statuses.toBeRated', 'Pending Review'),
      case_closed: tr('pbl.lawyerMatching.caseList.statuses.caseClosed', 'Closed'),
      case_cancelled: tr('statusTags.cancelled', 'Cancelled'),
      to_be_confirmed: tr('pbl.lawyerMatching.caseList.statuses.toBeConfirmed', 'Pending Confirmation'),
      pending: tr('pbl.lawyerMatching.caseList.statuses.pending', 'Pending'),
    }),
    [t]
  );
  const activeColumns = columns || defaultColumns;
  const labels = { ...defaultStatusLabels, ...statusLabels };
  const getStatusLabel = (item: TRow) =>
    translatePblCaseStatusLabel(t, item.backendStatus, item.statusLabel || labels[item.status] || item.status);
  const getCellDisplayValue = (column: PblCaseListColumn, item: TRow) => {
    const value = column.key === 'creationDate' ? formatPblDisplayDate(item.creationDate) : item[column.key];

    if (column.key === 'legalServiceNeeds') return translatePblDisplayList(t, value);
    if (column.key === 'enquiryCategory' || column.key === 'caseId') return translatePblDisplayValue(t, value);

    return value;
  };
  const isServerPaginated = !!onPageChange || !!onRowsPerPageChange;
  const activePage = currentPage ?? localPage;
  const activeRowsPerPage = rowsPerPage ?? localRowsPerPage;

  const visibleRows = useMemo(() => filterPblCaseListRows(rows, filter, keyword), [filter, keyword, rows]);
  const totalVisibleEntries = isServerPaginated ? totalEntries ?? visibleRows.length : visibleRows.length;
  const totalPages = Math.max(1, lastPage ?? Math.ceil(totalVisibleEntries / activeRowsPerPage));
  const pagedRows = useMemo(() => {
    if (isServerPaginated) return visibleRows;

    const start = (activePage - 1) * activeRowsPerPage;
    return visibleRows.slice(start, start + activeRowsPerPage);
  }, [activePage, activeRowsPerPage, isServerPaginated, visibleRows]);
  const hasPagedRows = pagedRows.length > 0;
  const firstVisibleRow = hasPagedRows ? (activePage - 1) * activeRowsPerPage + 1 : 0;
  const lastVisibleRow = hasPagedRows ? Math.min(firstVisibleRow + pagedRows.length - 1, totalVisibleEntries) : 0;
  const isBusy = requestState.status === 'loading' || requestState.status === 'refreshing';
  const isRefreshing = requestState.status === 'refreshing';
  const isInitialError = requestState.status === 'error' && (!requestState.hasStaleData || !hasPagedRows);
  const isStaleError = requestState.status === 'error' && requestState.hasStaleData && hasPagedRows;
  const areRequestControlsDisabled = isBusy || isInitialError;
  const showInlinePending = requestState.status === 'loading' || (isRefreshing && !hasPagedRows);
  const showEmpty = requestState.status === 'success' && !hasPagedRows;
  const showFooter = !showInlinePending && !isInitialError;
  const resolvedLoadingLabel = loadingLabel ?? tr('table.status.loading', 'Loading data…');
  const resolvedRefreshingLabel = refreshingLabel ?? tr('table.status.refreshing', 'Updating data…');
  const resolvedEmptyLabel = emptyLabel ?? tr('pbl.lawyerMatching.caseList.empty', 'No cases found.');
  const resolvedErrorLabel = errorLabel ?? tr('table.status.error', 'Unable to load data. Please try again.');
  const resolvedRetryLabel = retryLabel ?? tr('table.actions.retry', 'Retry');

  useEffect(() => {
    if (initialFilter !== 'all' || isPblDashboardCaseFilter(filter)) {
      setFilter(initialFilter);
    }
  }, [filter, initialFilter]);

  useEffect(() => {
    if (!isServerPaginated) {
      setLocalPage(1);
    }
  }, [activeRowsPerPage, filter, isServerPaginated, keyword]);

  useEffect(() => {
    if (!isServerPaginated && activePage > totalPages) {
      setLocalPage(totalPages);
    }
  }, [activePage, isServerPaginated, totalPages]);

  const goToPage = (page: number) => {
    if (isBusy) return;
    const nextPage = Math.min(Math.max(page, 1), totalPages);

    if (onPageChange) {
      onPageChange(nextPage);
      return;
    }

    setLocalPage(nextPage);
  };

  const changeRowsPerPage = (nextRowsPerPage: number) => {
    if (isBusy) return;
    if (onRowsPerPageChange) {
      onRowsPerPageChange(nextRowsPerPage);
      return;
    }

    setLocalRowsPerPage(nextRowsPerPage);
    setLocalPage(1);
  };

  const pageItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (activePage <= 3) {
      return [1, 2, 3, 'end-ellipsis' as const, totalPages];
    }

    if (activePage >= totalPages - 2) {
      return [1, 'start-ellipsis' as const, totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      'start-ellipsis' as const,
      activePage - 1,
      activePage,
      activePage + 1,
      'end-ellipsis' as const,
      totalPages,
    ];
  }, [activePage, totalPages]);

  const onRowKeyDown = (item: TRow) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (isBusy || !isRowClickable(item)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(item);
    }
  };

  return (
    <CaseListShell>
      <FilterBar isMobile={isMobile} $mobileCardLayout={mobileCardLayout} $tabletCardLayout={tabletCardLayout}>
        {filters?.map((item) => (
          <FilterButton
            key={item.value}
            type="button"
            active={filter === item.value}
            $mobileCardLayout={mobileCardLayout}
            $tabletCardLayout={tabletCardLayout}
            disabled={areRequestControlsDisabled}
            onClick={() => {
              setFilter(item.value);
              onFilterChange?.(item.value);
            }}
          >
            {item.label}
          </FilterButton>
        ))}
        <SearchInput
          $mobileCardLayout={mobileCardLayout}
          $tabletCardLayout={tabletCardLayout}
          aria-label={searchAriaLabel}
          placeholder={t('pbl.lawyerMatching.caseList.search.placeholder', { defaultValue: 'Search' })}
          value={keyword}
          disabled={areRequestControlsDisabled}
          onChange={(event) => setKeyword(event.target.value)}
        />
        <SearchIconButton type="button" aria-label={searchAriaLabel} disabled={areRequestControlsDisabled}>
          <img src={Search.SearchSVG} alt="" />
        </SearchIconButton>
        {primaryAction && (
          <ConfirmButton type="button" onClick={primaryAction.onClick} disabled={areRequestControlsDisabled}>
            {primaryAction.label}
          </ConfirmButton>
        )}
      </FilterBar>

      <TableCard $mobileCardLayout={mobileCardLayout} $tabletCardLayout={tabletCardLayout} $embedded={embedded}>
        {isRefreshing && hasPagedRows && <TableStatus status="refreshing" message={resolvedRefreshingLabel} compact />}
        {isStaleError && (
          <TableStatus
            status="error"
            message={resolvedErrorLabel}
            retryLabel={resolvedRetryLabel}
            onRetry={onRetry}
            compact
          />
        )}
        <TableScroll
          $mobileCardLayout={mobileCardLayout}
          $tabletCardLayout={tabletCardLayout}
          role="region"
          aria-label={tr('info.dataTable', 'Data table, scroll horizontally to see more columns.')}
          aria-busy={isBusy}
          tabIndex={0}
        >
          <CasesTable
            $mobileCardLayout={mobileCardLayout}
            $tabletCardLayout={tabletCardLayout}
            $stripedRows={stripedRows}
            $embedded={embedded}
            $isRefreshing={isRefreshing}
          >
            <thead>
              <tr>
                {activeColumns.map((column) => (
                  <th key={column.key} data-column={column.key}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!showInlinePending &&
                !isInitialError &&
                pagedRows.map((item) => {
                  const rowClickable = !isBusy && isRowClickable(item);

                  return (
                    <tr
                      key={item.id}
                      tabIndex={rowClickable ? 0 : undefined}
                      aria-disabled={!rowClickable}
                      onClick={() => {
                        if (rowClickable) onRowClick(item);
                      }}
                      onKeyDown={onRowKeyDown(item)}
                    >
                      {activeColumns.map((column) => {
                        const value = getCellDisplayValue(column, item);

                        return (
                          <td key={column.key} data-column={column.key} data-label={column.label}>
                            {column.key === 'status' ? (
                              <>
                              <StatusBadge
                                status={item.status}
                                $mobileCardLayout={mobileCardLayout}
                                $tabletCardLayout={tabletCardLayout}
                              >
                                {getStatusLabel(item)}
                              </StatusBadge>
                              {item.attentionLabel && <AttentionLabel>{item.attentionLabel}</AttentionLabel>}
                              </>
                            ) : (
                              value
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              {(showInlinePending || isInitialError || showEmpty) && (
                <tr className="table-status-row">
                  <td colSpan={activeColumns.length}>
                    {showInlinePending ? (
                      <TableStatus
                        status={isRefreshing ? 'refreshing' : 'loading'}
                        message={isRefreshing ? resolvedRefreshingLabel : resolvedLoadingLabel}
                      />
                    ) : isInitialError ? (
                      <TableStatus
                        status="error"
                        message={resolvedErrorLabel}
                        retryLabel={resolvedRetryLabel}
                        onRetry={onRetry}
                      />
                    ) : (
                      <TableStatus status="empty" message={resolvedEmptyLabel} />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </CasesTable>
        </TableScroll>

        {showFooter && (
          <TableFooter isMobile={isMobile} $mobileCardLayout={mobileCardLayout} $tabletCardLayout={tabletCardLayout}>
            <span>
              {t('pbl.lawyerMatching.caseList.pagination.showing', {
                defaultValue: 'Showing {{from}} to {{to}} of {{total}} entries',
                from: firstVisibleRow,
                to: lastVisibleRow,
                total: totalVisibleEntries,
              })}
            </span>
            <PaginationGroup $mobileCardLayout={mobileCardLayout} $tabletCardLayout={tabletCardLayout}>
              <span>{t('pbl.lawyerMatching.caseList.pagination.display', { defaultValue: 'Display' })}</span>
              <PageSelect
                value={String(activeRowsPerPage)}
                onChange={(event) => changeRowsPerPage(Number(event.target.value))}
                aria-label={t('pbl.lawyerMatching.caseList.pagination.rowsPerPage', { defaultValue: 'Rows per page' })}
                disabled={isBusy}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </PageSelect>
              <PageButton
                type="button"
                aria-label={tr('table.pagination.previousPage', 'Previous page')}
                disabled={isBusy || activePage <= 1}
                onClick={() => goToPage(activePage - 1)}
              >
                ‹
              </PageButton>
              {pageItems.map((item) =>
                typeof item === 'number' ? (
                  <PageButton
                    key={item}
                    type="button"
                    active={activePage === item}
                    disabled={isBusy}
                    onClick={() => goToPage(item)}
                  >
                    {item}
                  </PageButton>
                ) : (
                  <span key={item}>…</span>
                )
              )}
              <PageButton
                type="button"
                aria-label={tr('table.pagination.nextPage', 'Next page')}
                disabled={isBusy || activePage >= totalPages}
                onClick={() => goToPage(activePage + 1)}
              >
                ›
              </PageButton>
            </PaginationGroup>
          </TableFooter>
        )}
      </TableCard>
    </CaseListShell>
  );
};

const PblCaseList = <TRow extends PblCaseListRow>({
  title,
  backTo = -1,
  mobileCardLayout = true,
  ...contentProps
}: PblCaseListProps<TRow>) => {
  const navigate = useNavigate();
  //   @ts-ignore
  const { t } = useTranslation();
  const { isMobile } = useResize();

  const goBack = () => {
    if (typeof backTo === 'number') {
      navigate(backTo);
      return;
    }

    navigate(backTo);
  };

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={goBack}>
            {t('pbl.common.actions.back', { defaultValue: 'Back' })}
          </Button>
          <span>
            {t('pbl.lawyerVetting.detail.breadcrumb.home', { defaultValue: 'Home' })} / <strong>{title}</strong>
          </span>
        </TitleBar>
      }
    >
      <PblCaseListContent {...contentProps} mobileCardLayout={mobileCardLayout} />
    </PageContainer>
  );
};

export default PblCaseList;
